import db from "../db/database";
import {
  fetchFromBusinessCentral,
  parseImportResponse,
  sendToBusinessCentral,
} from "../utils/businessCentralUtils";
import { formatAxiosError } from "../utils/common";
import { getPendingSync, isOnline } from "./syncService";
import {
  conAvisoDeBajaRechazada,
  enviarLote,
  motivoDeGuardadoFallido,
  resumenDelLote,
  separarPendientes,
} from "./loteSyncHelpers";
import { anotarError, anotarFila, anotarInicioDeLote, anotarResumenDelLote } from "./syncLog";

// Nombre con el que esta entidad aparece en las líneas de consola.
const ENTIDAD = "usuarios";

// Nombre completo para mostrar, armado a partir de los dos apellidos reales.
// `lastName` se sigue guardando con este valor porque lo leen diecisiete
// pantallas y el índice de búsqueda, pero el origen de la verdad son los dos
// campos separados.
export const componerApellidos = (primerApellido, segundoApellido) =>
  [primerApellido, segundoApellido]
    .map((parte) => (parte || "").trim())
    .filter(Boolean)
    .join(" ");

// Un usuario en el formato que espera BC.
const usuarioParaBC = (user) => {
  // Los dos apellidos viajan tal cual, cada uno en su campo.
  //
  // Antes se guardaba un solo `lastName` concatenado y acá se partía por el
  // primer espacio. Eso rompía cualquier apellido de más de una palabra:
  // "BUSINESS CENTRAL" | "" volvía a BC como "BUSINESS" | "CENTRAL", y como el
  // pull los vuelve a pegar, en pantalla no se notaba. BC nunca partió nada --
  // recibe y devuelve los dos campos intactos.
  //
  // El `?? ` cubre los registros que todavía no pasaron por la migración a la
  // versión 18 del esquema local.
  const primerApellido = user.primerApellido ?? user.lastName ?? "";
  const segundoApellido = user.segundoApellido ?? "";

  const transformedUser = {
    No_: user.No || "TEMP", // Si no tiene No, usamos TEMP para que BC asigne uno
    First_Family_Name: primerApellido,
    Second_Family_Name: segundoApellido,
    Name: user.firstName,
    E_Mail: user.email,
    Company_E_Mail: user.email,
    Deleted: user.deleted,
    Password: user.password, // La contraseña se enviará si existe
    Admin: user.Admin || false, // Mapear Admin local a Admin de BC
    // Mismos nombres que devuelve el GET de ListaEmpleados; sin confirmar
    // todavía si Importaciones_insertaEmpleados (POST) los acepta igual
    Tienda: user.Tienda || "",
    Grupo: user.Grupo || "",
  };

  return transformedUser;
};

// Payload de la acción, con todos los usuarios que se manden en la misma llamada.
//
// Importaciones_insertaEmpleados siempre recibió un array y lo recorre con un
// foreach; el TPV era el que mandaba uno solo por viaje. Cada usuario se procesa
// aislado del lado de BC (TryProcesarEmpleado), así que un rechazo no arrastra a
// los demás y la respuesta trae un resultado por registro, en el mismo orden.
const transformUsersForBC = (users) => ({
  data: JSON.stringify({ Employees: users.map(usuarioParaBC) }),
});

const transformUserForBC = (user) => transformUsersForBC([user]);

// BC rechazó la baja: se revierte el borrado local para que el usuario vuelva a
// aparecer en el listado y local y BC no queden divergentes. pendingSync queda
// en 0 a propósito -- no hay nada pendiente que mandar, y así el pull de
// ListaEmpleados lo vuelve a refrescar con lo que BC tiene hoy.
const restoreDeletedUser = async (user, reason) => {
  await db.users.update(user.id, {
    deleted: false,
    pendingSync: 0,
    lastModified: new Date(),
    // El motivo que devolvió BC se guarda en el propio registro. Es lo único
    // que sobrevive a la corrida: el registro reaparece en el listado con
    // pendingSync 0, o sea con aspecto de estar perfectamente sincronizado, y
    // sin esto no queda nadie que pueda explicar por qué volvió. Lo lee
    // RecordSyncTag. Ver SANTPV-59.
    syncError: reason || "Business Central no aplicó la baja.",
  });
};

// Nombre con el que se identifica al usuario en el cuadro de resultados. Si no
// tiene nombre -- pasa: seis cajeros del ambiente tienen el Nombre en blanco --
// se cae al No., que es su identificador real.
const nombreDeUsuario = (user) =>
  [user.firstName, user.lastName].filter(Boolean).join(" ") || user.No || "(sin nombre)";

// Sincroniza un solo usuario contra BC, para el botón de sincronizar de cada
// fila del listado. Mismo criterio que syncUsers -- misma acción de BC, mismo
// parseo de la respuesta y misma reversión del borrado cuando BC lo rechaza --
// pero devuelve { ok, message } para poder mostrarle el resultado a quien
// apretó el botón, en vez de acumularlo en un resumen.
export const syncUserById = async (userId) => {
  if (!isOnline()) {
    return { ok: false, message: "No hay conexión a internet" };
  }

  try {
    const user = await db.users.get(userId);
    if (!user) {
      return { ok: false, message: "Usuario no encontrado" };
    }

    if (user.deleted) {
      // Nunca llegó a BC: solo existe en local.
      if (!user.No) {
        await db.users.delete(user.id);
        return { ok: true, message: "Usuario eliminado" };
      }

      const response = await sendToBusinessCentral(
        "Importaciones_insertaEmpleados",
        transformUserForBC(user),
        true
      );
      const result = parseImportResponse(response, user.No);

      if (!result.ok) {
        await restoreDeletedUser(user, result.message);
        return {
          ok: false,
          message:
            result.message || "Business Central no aplicó la baja. El usuario sigue en el listado.",
        };
      }

      await db.users.delete(user.id);
      return { ok: true, message: "Usuario dado de baja en Business Central" };
    }

    const response = await sendToBusinessCentral(
      "Importaciones_insertaEmpleados",
      transformUserForBC(user),
      true
    );
    const result = parseImportResponse(response, user.No || "TEMP");

    if (!result.ok || !result.assignedNo) {
      return {
        ok: false,
        message: result.message || "Respuesta inválida de Business Central",
      };
    }

    // Se lee antes del update, que es justo lo que lo borra.
    const teniaBajaRechazada = Boolean(user.syncError);

    await db.users.update(user.id, {
      No: result.assignedNo,
      lastModified: new Date(),
      lastSynced: new Date(),
      pendingSync: 0,
      syncError: null,
    });

    return {
      ok: true,
      message: conAvisoDeBajaRechazada("Usuario sincronizado correctamente.", teniaBajaRechazada),
    };
  } catch (error) {
    anotarError(ENTIDAD, `falló el envío del registro ${userId}`, error);
    return {
      ok: false,
      message: formatAxiosError(error, "No se pudo sincronizar el usuario"),
    };
  }
};

export const syncUsers = async () => {
  if (!isOnline()) {
    return false;
  }

  // Usuarios cuyo push a BC falló (o fue ignorado por BC) en esta corrida.
  // Cada entrada es { user, action: "delete" | "save", reason }, para poder
  // decir en el mensaje final qué se rompió y por qué.
  // Importaciones_insertaEmpleados soporta hoy las tres operaciones. Durante un
  // tiempo sólo dio de alta: con un No_ ya asignado devolvía 200 con
  // {"value": ""} sin aplicar nada, así que la edición de un usuario ya
  // sincronizado se perdía en silencio en el pull siguiente. Verificado en el
  // AL (TryProcesarEmpleado, 2026-08-21): la rama de alta hace Insert y marca
  // 'created', la de baja valida movimientos antes de Delete y marca 'deleted',
  // y el resto del cuerpo hace Modify(true) y marca 'updated'.
  const failedUsers = [];

  // Una fila por registro enviado, para el cuadro que se le muestra a quien
  // apretó Sincronizar. Incluye los correctos, no sólo los fallos.
  const detalle = [];

  try {
    const pendingUsers = await getPendingSync("users");

    if (pendingUsers.length > 0) {
      const { soloLocales, aEnviar } = separarPendientes(pendingUsers);

      for (const user of soloLocales) {
        await db.users.delete(user.id);
        detalle.push(
          anotarFila(
            ENTIDAD,
            {
              registro: nombreDeUsuario(user),
              no: null,
              accion: "deleted",
              ok: true,
              mensaje: "Eliminado sólo en local: nunca llegó a Business Central",
            },
            user.id,
            "baja-local"
          )
        );
      }

      if (aEnviar.length > 0) {
        anotarInicioDeLote(ENTIDAD, aEnviar.length);

        // UNA llamada para todos los pendientes, en vez de una por usuario.
        const lote = await enviarLote(
          "Importaciones_insertaEmpleados",
          transformUsersForBC(aEnviar),
          aEnviar.length,
          "usuarios"
        );

        // El cruce es por posición: BC devuelve los resultados en el mismo orden
        // en que recibió los usuarios. Por No_ no se podría, porque todas las
        // altas se mandan como "TEMP" y volverían indistinguibles entre sí.
        for (let indice = 0; indice < aEnviar.length; indice++) {
          const user = aEnviar[indice];
          const resultado = lote.results[indice];
          let traiaBajaRechazada = false;

          if (user.deleted) {
            if (resultado.ok) {
              await db.users.delete(user.id);
            } else {
              // BC no aplicó la baja: el usuario NO se borra en local, se
              // revierte el borrado y el motivo queda en el registro.
              await restoreDeletedUser(user, resultado.message);
              failedUsers.push({ user, action: "delete", reason: resultado.message });
            }
          } else if (resultado.ok && resultado.assignedNo) {
            // Se anota antes del update, que es lo que borra el motivo anterior.
            traiaBajaRechazada = Boolean(user.syncError);

            await db.users.update(user.id, {
              No: resultado.assignedNo,
              lastModified: new Date(),
              lastSynced: new Date(),
              pendingSync: 0,
              syncError: null,
            });
          } else {
            const motivo = motivoDeGuardadoFallido(resultado);
            failedUsers.push({ user, action: "save", reason: motivo });
          }

          detalle.push(
            anotarFila(
              ENTIDAD,
              {
                registro: nombreDeUsuario(user),
                no: resultado.assignedNo || user.No || null,
                accion: resultado.action || (user.deleted ? "deleted" : "saved"),
                ok: user.deleted ? resultado.ok : resultado.ok && Boolean(resultado.assignedNo),
                mensaje: conAvisoDeBajaRechazada(
                  resultado.message || "",
                  traiaBajaRechazada
                ).trim(),
              },
              user.id,
              user.deleted ? "baja" : undefined
            )
          );
        }
      }
    }

    // Obtener usuarios del servidor después de sincronizar los pendientes
    const serverUsers = await fetchFromBusinessCentral("ListaEmpleados", "Usuario_TPV eq true");
    const localUsers = await db.users.toArray();

    // Mapa para buscar usuarios locales por No rápidamente
    const localUsersByNo = {};
    localUsers.forEach((user) => {
      if (user.No) {
        localUsersByNo[user.No] = user;
      }
    });

    // Actualizar usuarios existentes y añadir nuevos desde el servidor
    for (const serverUser of serverUsers) {
      // Verificar si el usuario ya existe localmente
      const localUser = localUsersByNo[serverUser.No];

      if (localUser) {
        // Actualizar usuario existente si no está pendiente de sincronización
        if (localUser.pendingSync === 0) {
          await db.users.update(localUser.id, {
            firstName: serverUser.Name,
            primerApellido: serverUser.First_Family_Name || "",
            segundoApellido: serverUser.Second_Family_Name || "",
            lastName: componerApellidos(
              serverUser.First_Family_Name,
              serverUser.Second_Family_Name
            ),
            email: serverUser.E_Mail,
            password: serverUser.Password,
            Admin: serverUser.Admin || false, // Mapear Admin de BC a Admin local
            // Tienda/Grupo son asignados desde BC (no editables en el TPV), se guardan solo de lectura
            Tienda: serverUser.Tienda || "",
            Grupo: serverUser.Grupo || "",
            lastModified: new Date(),
            lastSynced: new Date(),
          });
        }
      } else {
        // Añadir nuevo usuario del servidor si no existe localmente
        await db.users.add({
          No: serverUser.No,
          firstName: serverUser.Name,
          primerApellido: serverUser.First_Family_Name || "",
          segundoApellido: serverUser.Second_Family_Name || "",
          lastName: componerApellidos(serverUser.First_Family_Name, serverUser.Second_Family_Name),
          email: serverUser.E_Mail,
          password: serverUser.Password,
          Admin: serverUser.Admin || false, // Mapear Admin de BC a Admin local
          Tienda: serverUser.Tienda || "",
          Grupo: serverUser.Grupo || "",
          lastSynced: new Date(),
          pendingSync: 0,
          syncError: null,
          deleted: false,
        });
      }
    }

    // Cierra el lote en consola con el mismo recuento que ve quien apretó

    // Sincronizar. Antes había que sumar las líneas a ojo.

    const resumen = resumenDelLote(detalle);

    anotarResumenDelLote(ENTIDAD, resumen);

    if (failedUsers.length > 0) {
      const deletes = failedUsers.filter((f) => f.action === "delete");
      const saves = failedUsers.filter((f) => f.action === "save");

      const parts = [];
      if (deletes.length > 0) {
        parts.push(
          `${deletes.length} usuario(s) no se pudieron eliminar en Business Central y siguen en el listado`
        );
      }
      if (saves.length > 0) {
        parts.push(
          `${saves.length} usuario(s) con ediciones no se pudieron guardar en Business Central`
        );
      }

      // Motivo concreto que devolvió BC, si lo hubo: explica bastante mejor que
      // el mensaje genérico por qué no se aplicó el cambio.
      const reason = failedUsers.find((f) => f.reason)?.reason;

      return {
        success: false,
        failedCount: failedUsers.length,
        message: reason ? `${parts.join(". ")}: ${reason}` : parts.join(". "),
        lote: resumen,
      };
    }

    // Se devuelve el detalle también cuando todo salió bien: la pantalla muestra
    // el mismo cuadro en los dos casos, y `executeWithRetry` sólo mira
    // `success === false`, así que un objeto acá no cambia nada para syncAll.
    return { success: true, lote: resumen };
  } catch (error) {
    anotarError(ENTIDAD, "falló la sincronización", error);
    return false;
  }
};
